import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, School as SchoolIcon, Copy, Loader2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getDataQuality } from '../api';

const numFmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));

/** Data Quality Center — surfaces every issue the parser preserved without
 *  losing data: schools without ministerial numbers, duplicate ministerial
 *  ids, and rows missing unit prices. */
export default function DataQualityCenter() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('missing');
  useEffect(() => {
    setLoading(true);
    getDataQuality().then(setData).finally(() => setLoading(false));
  }, []);

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="data-quality-page">
        <div className="mb-6">
          <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>DATA QUALITY CENTER</div>
          <h1 className="se-title mt-1">مركز جودة البيانات</h1>
          <div className="se-subtitle">كل السجلات محفوظة — هذه هي الحقول التي تحتاج إلى مراجعة يدوية</div>
        </div>

        {loading ? (
          <div className="se-panel p-12 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : data ? (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard label="إجمالي السجلات" value={numFmt(data.summary.total_records)} icon={ShieldCheck} tint="#38D6C7" testid="dq-total" />
              <StatCard label="مدارس بدون رقم وزاري" value={numFmt(data.summary.schools_missing_ministerial)} icon={SchoolIcon} tint="#F59E0B" testid="dq-missing-id" />
              <StatCard label="مجموعات أرقام مكررة" value={numFmt(data.summary.duplicate_ministerial_groups)} icon={Copy} tint="#EF4444" testid="dq-duplicates" />
              <StatCard label="سجلات بدون سعر وحدة" value={numFmt(data.summary.records_missing_price)} icon={AlertTriangle} tint="#EF4444" testid="dq-no-price" />
            </section>

            <div className="flex gap-2 mb-4" data-testid="dq-tabs">
              <TabBtn active={tab === 'missing'} onClick={() => setTab('missing')} count={data.schools_missing_ministerial.length} testid="dq-tab-missing">مدارس بدون رقم وزاري</TabBtn>
              <TabBtn active={tab === 'dupes'} onClick={() => setTab('dupes')} count={data.duplicate_ministerial.length} testid="dq-tab-dupes">أرقام وزارية مكررة</TabBtn>
              <TabBtn active={tab === 'price'} onClick={() => setTab('price')} count={data.records_missing_price.length} testid="dq-tab-price">أصناف بدون سعر</TabBtn>
            </div>

            {tab === 'missing' && <MissingIdTable rows={data.schools_missing_ministerial} />}
            {tab === 'dupes'   && <DupeTable rows={data.duplicate_ministerial} />}
            {tab === 'price'   && <PriceTable rows={data.records_missing_price} />}
          </>
        ) : (
          <div className="se-panel p-12 text-center text-slate-400">تعذر تحميل البيانات</div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="se-kpi" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="se-kpi-label">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}22`, border: `1px solid ${tint}44` }}>
          <Icon size={15} style={{ color: tint }} />
        </div>
      </div>
      <div className="se-kpi-value">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, count, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors"
      style={active
        ? { background: 'rgba(109,93,251,0.25)', border: '1px solid rgba(109,93,251,0.55)', color: '#F1EEFB' }
        : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#94A3B8' }}>
      {children} <span className="opacity-60 mr-2">({numFmt(count)})</span>
    </button>
  );
}

function MissingIdTable({ rows }) {
  if (!rows?.length) return <div className="se-panel p-8 text-center text-slate-500">لا توجد مدارس بدون رقم وزاري 🎉</div>;
  return (
    <div className="se-panel overflow-x-auto" data-testid="dq-missing-table">
      <table className="se-table">
        <thead><tr>
          <th>المدرسة</th><th>الإدارة التعليمية</th><th>المدينة</th><th>المورد</th>
          <th className="text-center">السجلات</th><th className="text-center">الكمية</th><th className="text-center">القيمة</th>
          <th>المعرف الداخلي</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.school_internal_id}>
              <td className="font-semibold">{r.school_name || '—'}</td>
              <td>{r.governorate || '—'}</td>
              <td>{r.region || '—'}</td>
              <td>{r.supplier || '—'}</td>
              <td className="text-center tabular-nums">{numFmt(r.records)}</td>
              <td className="text-center tabular-nums font-bold" style={{ color: '#9D8CFF' }}>{numFmt(r.allocated_qty)}</td>
              <td className="text-center tabular-nums">{moneyFmt(r.allocated_value)}</td>
              <td className="text-[10px] font-mono text-slate-500">{r.school_internal_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DupeTable({ rows }) {
  if (!rows?.length) return <div className="se-panel p-8 text-center text-slate-500">لا توجد أرقام وزارية مكررة</div>;
  return (
    <div className="se-panel overflow-x-auto" data-testid="dq-dupes-table">
      <table className="se-table">
        <thead><tr>
          <th>الرقم الوزاري</th><th>عدد المسميات</th><th>الأسماء</th><th>المدن</th><th className="text-center">السجلات</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="font-mono font-bold">{r.school_id}</td>
              <td className="text-center tabular-nums" style={{ color: '#EF4444' }}>{r.name_count}</td>
              <td className="max-w-md truncate" title={(r.names || []).join(' · ')}>{(r.names || []).slice(0, 3).join(' · ')}</td>
              <td>{(r.regions || []).filter(Boolean).slice(0, 3).join(' · ')}</td>
              <td className="text-center tabular-nums">{numFmt(r.records)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PriceTable({ rows }) {
  if (!rows?.length) return <div className="se-panel p-8 text-center text-slate-500">كل الأصناف لها سعر وحدة 🎉</div>;
  return (
    <div className="se-panel overflow-x-auto" data-testid="dq-price-table">
      <table className="se-table">
        <thead><tr>
          <th>المورد</th><th>الصنف</th><th className="text-center">السجلات</th><th className="text-center">الكمية</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="font-semibold">{r.supplier || '—'}</td>
              <td>{r.item || '—'}</td>
              <td className="text-center tabular-nums">{numFmt(r.records)}</td>
              <td className="text-center tabular-nums">{numFmt(r.qty)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
